'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import SquaredBox from '@/components/ui/squared-box';
import { ArrowPrevIcon } from '@/components/icons/arrow-prev';
import { CalendarGhostIcon } from '@/components/icons/calendar';
import { PbmEmbeddedShell } from './pbm-embedded-shell';
import Header from './pbm-header';
import type { PBMActivation } from '@/types';
import { ptBr } from '@/lib/pt-br/messages';
import { fetchZipLocation } from '@/lib/actions/postal-code-location';
import { removeMask } from '@/lib/utils/validation';
import { dateMask } from '@/lib/utils/mask';
import {
  brDateToIso,
  buildInitialValues,
  checkboxCheckedValue,
  checkboxUncheckedValue,
  fieldUsesSelect,
  flattenActivationFields,
  getFieldAutoComplete,
  getFieldColSpan,
  getFieldLabel,
  getFieldPlaceholder,
  getOptions,
  getPathValue,
  getPersonTypeValue,
  groupFieldsByRoot,
  humanizeFieldKey,
  isCheckboxChecked,
  isCheckboxField,
  isCoveredByLgpdCheckbox,
  isDateTimeField,
  isMandatory,
  isoDateOnly,
  isoToBrDate,
  isResponsiblePersonType,
  isSelfPatientPersonType,
  prepareActivationPayload,
  setPathValue,
  sizeLimit,
  stripDependentIfSelfPatient,
  validateActivationValues,
  type FlatActivationField,
} from './pbm-activation-fields';

/**
 * Each field spans 3 rows of the section grid (label / control / error) via
 * subgrid, so controls in the same row stay aligned when a label wraps.
 */
const FIELD_CELL_CLASS = 'row-span-3 grid w-full grid-rows-subgrid gap-y-0';

const TYPED_DATE_MEDIA_QUERY = '(max-width: 767px), (pointer: coarse)';

/** Mobile/touch devices type dates with a mask instead of the native picker. */
function usePrefersTypedDate(): boolean {
  const [prefersTyped, setPrefersTyped] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(TYPED_DATE_MEDIA_QUERY);
    const update = () => setPrefersTyped(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  return prefersTyped;
}

/**
 * Masked DD/MM/AAAA text input (same typing UX as the rest of the site).
 * On desktop a calendar button opens the native picker via a hidden date input,
 * so the typed field is never controlled by the browser's date segments.
 */
function ActivationDateInput({
  inputProps,
  value,
  iso,
  label,
  showPicker,
  onChange,
}: Readonly<{
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
  value: string;
  iso: string;
  label: string;
  showPicker: boolean;
  onChange: (value: string) => void;
}>) {
  const pickerRef = useRef<HTMLInputElement>(null);

  const openPicker = () => {
    const picker = pickerRef.current;
    if (!picker) return;
    try {
      if (typeof picker.showPicker === 'function') {
        picker.showPicker();
        return;
      }
    } catch {
      // showPicker can throw without user activation; fall through
    }
    picker.click();
  };

  return (
    <div className="relative">
      <input
        {...inputProps}
        type="text"
        inputMode="numeric"
        placeholder="DD/MM/AAAA"
        maxLength={10}
        value={iso ? isoToBrDate(iso) : value}
        onChange={(e) => {
          const raw = e.target.value.trim();
          // Browser autofill may inject ISO (YYYY-MM-DD) directly
          const autofilled = isoDateOnly(raw);
          if (autofilled) {
            onChange(autofilled);
            return;
          }
          const masked = dateMask(raw);
          onChange(brDateToIso(masked) ?? masked);
        }}
        className={`${INPUT_CLASS} ${showPicker ? 'pr-11' : ''}`}
      />
      {showPicker ? (
        <>
          <input
            ref={pickerRef}
            type="date"
            tabIndex={-1}
            aria-hidden="true"
            value={iso}
            onChange={(e) => {
              if (e.target.value) onChange(e.target.value);
            }}
            className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
          />
          <button
            type="button"
            onClick={openPicker}
            aria-label={`Abrir calendário: ${label}`}
            className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-md text-body transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
          >
            <CalendarGhostIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </>
      ) : null}
    </div>
  );
}

const INPUT_CLASS =
  'h-11 w-full rounded-md border border-border-base bg-light px-3 text-sm text-heading placeholder:text-body/50 transition-colors focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-60';

function FieldControl({
  field,
  value,
  error,
  onChange,
  autoFocus,
  typedDate,
}: Readonly<{
  field: FlatActivationField;
  value: unknown;
  error?: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  typedDate: boolean;
}>) {
  const id = `pbm-act-${field.path.join('-')}`;
  const label = getFieldLabel(field);
  const required = isMandatory(field.meta);
  const type = String(field.meta.type || 'string').toLowerCase();
  const strValue = value === undefined || value === null ? '' : String(value);
  const options = getOptions(field.meta, field.key);
  const placeholder = getFieldPlaceholder(field.key);
  const autoComplete = getFieldAutoComplete(field.key);
  const colSpan = getFieldColSpan(field.key);
  const maxLength = isDateTimeField(field)
    ? undefined
    : sizeLimit(field.meta, 'max');

  if (isCheckboxField(field)) {
    const checked = isCheckboxChecked(value);
    return (
      <div className={`${FIELD_CELL_CLASS} ${colSpan || 'md:col-span-2'}`}>
        <label
          htmlFor={id}
          className="flex cursor-pointer items-start gap-3 rounded-md border border-transparent p-1 text-sm text-heading hover:border-border-200"
        >
          <input
            id={id}
            name={field.path.join('.')}
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-accent,#009FDF)] focus-visible:ring-2 focus-visible:ring-accent/30"
            checked={checked}
            onChange={(e) =>
              onChange(
                e.target.checked
                  ? checkboxCheckedValue()
                  : checkboxUncheckedValue(),
              )
            }
            autoFocus={autoFocus}
            aria-invalid={Boolean(error)}
            aria-required={required}
            aria-describedby={error ? `${id}-error` : undefined}
          />
          <span>
            {label}
            {required ? (
              <span className="text-red-500" aria-hidden="true">
                {' '}
                *
              </span>
            ) : (
              <span className="ml-1 text-xs font-normal text-body/70">
                (opcional)
              </span>
            )}
          </span>
        </label>
        {error ? (
          <p id={`${id}-error`} className="mt-1 text-xs text-red-500" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  const commonLabel = (
    <label
      htmlFor={id}
      className="mb-1.5 block self-end text-sm font-medium text-heading"
    >
      {label}
      {required ? (
        <span className="text-red-500" aria-hidden="true">
          {' '}
          *
        </span>
      ) : (
        <span className="ml-1 text-xs font-normal text-body/70">(opcional)</span>
      )}
    </label>
  );

  if (fieldUsesSelect(field.meta, field.key)) {
    return (
      <div className={`${FIELD_CELL_CLASS} ${colSpan}`}>
        {commonLabel}
        <select
          id={id}
          name={field.path.join('.')}
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={autoFocus}
          aria-invalid={Boolean(error)}
          aria-required={required}
          aria-describedby={error ? `${id}-error` : undefined}
          className={INPUT_CLASS}
        >
          <option value="">Selecione…</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {error ? (
          <p id={`${id}-error`} className="mt-1 text-xs text-red-500" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  const errorNode = error ? (
    <p id={`${id}-error`} className="mt-1 text-xs text-red-500" role="alert">
      {error}
    </p>
  ) : null;

  if (isDateTimeField(field)) {
    const iso = isoDateOnly(strValue);
    const dateProps = {
      id,
      name: field.path.join('.'),
      autoComplete,
      autoFocus,
      'aria-invalid': Boolean(error),
      'aria-required': required,
      'aria-describedby': error ? `${id}-error` : undefined,
      className: INPUT_CLASS,
    };

    return (
      <div className={`${FIELD_CELL_CLASS} ${colSpan}`}>
        {commonLabel}
        <ActivationDateInput
          inputProps={dateProps}
          value={strValue}
          iso={iso}
          label={label}
          showPicker={!typedDate}
          onChange={onChange}
        />
        {errorNode}
      </div>
    );
  }

  const inputType =
    type === 'numeric'
        ? 'tel'
        : field.key.toLowerCase() === 'email'
          ? 'email'
          : 'text';

  const inputMode =
    type === 'numeric' ||
    ['DDDcellPhone', 'cellPhone', 'postalCode', 'addressNumber', 'id'].includes(
      field.key,
    )
      ? 'numeric'
      : undefined;

  return (
    <div className={`${FIELD_CELL_CLASS} ${colSpan}`}>
      {commonLabel}
      <input
        id={id}
        name={field.path.join('.')}
        type={inputType}
        inputMode={inputMode}
        value={strValue}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        placeholder={placeholder || undefined}
        autoComplete={autoComplete}
        spellCheck={field.key.toLowerCase() === 'email' ? false : undefined}
        autoFocus={autoFocus}
        aria-invalid={Boolean(error)}
        aria-required={required}
        aria-describedby={error ? `${id}-error` : undefined}
        className={INPUT_CLASS}
      />
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-xs text-red-500" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function resolveSeedEan(activation: PBMActivation, productEan?: string): string {
  if (productEan) return String(productEan).replace(/\D/g, '') || productEan;
  const eanField = flattenActivationFields(activation.fields || {}).find(
    (f) => f.key.toLowerCase() === 'ean',
  );
  if (eanField?.meta?.value != null && eanField.meta.value !== '') {
    return String(eanField.meta.value);
  }
  return '';
}

function resolveBanner(activation: PBMActivation): string {
  const bannerField = flattenActivationFields(activation.fields || {}).find(
    (f) => f.key.toLowerCase() === 'banner',
  );
  if (bannerField?.meta?.value != null) {
    return String(bannerField.meta.value);
  }
  return '';
}

export function BoxActivationForm({
  activation,
  attempt,
  serverError,
  isSubmitting,
  programName,
  productEan,
  documentCpf,
  onBack,
  onSubmit,
  embedded,
}: Readonly<{
  activation: PBMActivation;
  attempt: number;
  serverError?: string | null;
  isSubmitting?: boolean;
  programName?: string;
  productEan?: string;
  documentCpf?: string;
  onBack: () => void;
  onSubmit: (userPayload: Record<string, unknown>) => void;
  embedded?: boolean;
}>) {
  const fields = activation.fields || {};
  const groups = useMemo(() => groupFieldsByRoot(fields), [fields]);

  const seeds = useMemo(() => {
    const centralFromSchema = flattenActivationFields(fields).find(
      (f) => f.key.toLowerCase() === 'centralnumber',
    )?.meta?.value;
    const centralRaw = activation.session?.centralNumber ?? centralFromSchema;
    return {
      ean: resolveSeedEan(activation, productEan),
      centralNumber:
        centralRaw === undefined || centralRaw === null
          ? undefined
          : (centralRaw as string | number),
      banner: resolveBanner(activation),
      holderId: documentCpf,
    };
  }, [fields, productEan, documentCpf, activation]);

  const [values, setValues] = useState<Record<string, unknown>>(() =>
    buildInitialValues(fields, seeds),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lgpdAccepted, setLgpdAccepted] = useState(false);
  const prefersTypedDate = usePrefersTypedDate();
  const formRef = useRef<HTMLFormElement>(null);
  // Keep user input across activation retries (new session/fields from API errors).
  // Only re-seed system values (ean, centralNumber, holderId) without wiping the form.
  const hasUserEditsRef = useRef(false);

  const personType = getPersonTypeValue(values);
  const showDependent = isResponsiblePersonType(personType);
  const visibleGroups = useMemo(
    () =>
      groups.filter(({ groupKey }) => {
        if (groupKey === 'dependent' && !showDependent) return false;
        return true;
      }),
    [groups, showDependent],
  );
  const firstFieldPath = visibleGroups[0]?.fields[0]?.path.join('.') ?? null;

  useEffect(() => {
    if (!hasUserEditsRef.current) {
      setValues(buildInitialValues(fields, seeds));
      return;
    }
    // Retry / refreshed activation: patch system seeds only
    setValues((prev) => {
      let next = prev;
      if (seeds.ean) {
        next = setPathValue(next, ['product', 'ean'], seeds.ean);
      }
      if (seeds.centralNumber != null) {
        next = setPathValue(
          next,
          ['control', 'centralnumber'],
          seeds.centralNumber,
        );
        next = setPathValue(
          next,
          ['control', 'centralNumber'],
          seeds.centralNumber,
        );
      }
      if (seeds.holderId) {
        next = setPathValue(
          next,
          ['consumer', 'holderId'],
          String(seeds.holderId).replace(/\D/g, ''),
        );
      }
      return next;
    });
  }, [fields, seeds]);

  const postalCodeRaw = getPathValue(values, ['consumer', 'postalCode']);
  const postalCodeDigits = removeMask(String(postalCodeRaw ?? ''));

  useQuery(
    ['pbm-activation-cep', postalCodeDigits],
    () => fetchZipLocation(postalCodeDigits),
    {
      enabled: postalCodeDigits.length === 8,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        const content = data?.content;
        if (!content) return;
        hasUserEditsRef.current = true;
        setValues((prev) => {
          let next = prev;
          if (content.street) {
            next = setPathValue(next, ['consumer', 'streetAddress'], content.street);
          }
          if (content.city) {
            next = setPathValue(next, ['consumer', 'cityName'], content.city);
          }
          if (content.state_short) {
            next = setPathValue(
              next,
              ['consumer', 'stateCode'],
              content.state_short,
            );
          }
          if (content.bairro) {
            next = setPathValue(next, ['consumer', 'cityRegion'], content.bairro);
          }
          return next;
        });
        setErrors((prev) => {
          const copy = { ...prev };
          delete copy['consumer.streetAddress'];
          delete copy['consumer.cityName'];
          delete copy['consumer.stateCode'];
          delete copy['consumer.cityRegion'];
          return copy;
        });
      },
    },
  );

  const requiresLgpd = Boolean(
    fields.acceptLgpd ||
      activation.url_accept_term ||
      activation.url_text_accept_term,
  );
  const termsUrl =
    activation.url_text_accept_term || activation.url_accept_term || '';

  const handleChange = (path: string[], next: string) => {
    hasUserEditsRef.current = true;
    setValues((prev) => setPathValue(prev, path, next));

    const pathKey = path.join('.');
    setErrors((prev) => {
      const copy = { ...prev };
      delete copy[pathKey];
      if (
        path.length === 2 &&
        path[0] === 'consumer' &&
        path[1] === 'personType' &&
        isSelfPatientPersonType(next)
      ) {
        for (const key of Object.keys(copy)) {
          if (key.includes('.dependent.') || key.startsWith('dependent.')) {
            delete copy[key];
          }
        }
      }
      return copy;
    });
  };

  const focusFirstError = (nextErrors: Record<string, string>) => {
    const firstKey = Object.keys(nextErrors)[0];
    if (!firstKey || !formRef.current) return;
    const byName = formRef.current.querySelector<HTMLElement>(
      `[name="${firstKey}"]`,
    );
    if (byName) {
      byName.focus();
      return;
    }
    const id = `pbm-act-${firstKey.replace(/\./g, '-')}`;
    formRef.current.querySelector<HTMLElement>(`#${id}`)?.focus();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors = validateActivationValues(fields, values);
    if (requiresLgpd && !lgpdAccepted) {
      nextErrors.acceptLgpd = ptBr('pbm-activation-lgpd-required');
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      focusFirstError(nextErrors);
      return;
    }

    const payload = prepareActivationPayload(
      fields,
      stripDependentIfSelfPatient(values),
    );

    if (seeds.ean) {
      const existing =
        payload.product && typeof payload.product === 'object'
          ? (payload.product as Record<string, unknown>)
          : {};
      payload.product = { ...existing, ean: existing.ean || seeds.ean };
    }

    if (seeds.centralNumber != null) {
      const existing =
        payload.control && typeof payload.control === 'object'
          ? (payload.control as Record<string, unknown>)
          : {};
      // Interplayers schema uses lowercase `centralnumber`
      const hasLower = 'centralnumber' in existing;
      payload.control = {
        ...existing,
        ...(hasLower
          ? { centralnumber: existing.centralnumber ?? seeds.centralNumber }
          : { centralNumber: existing.centralNumber ?? seeds.centralNumber }),
      };
    }

    if (seeds.holderId) {
      const existing =
        payload.consumer && typeof payload.consumer === 'object'
          ? (payload.consumer as Record<string, unknown>)
          : {};
      payload.consumer = {
        ...existing,
        holderId:
          existing.holderId || String(seeds.holderId).replace(/\D/g, ''),
      };
    }

    if (requiresLgpd) {
      const existing =
        payload.acceptLgpd && typeof payload.acceptLgpd === 'object'
          ? (payload.acceptLgpd as Record<string, unknown>)
          : {};
      const termFromFields =
        getPathValue(buildInitialValues(fields, seeds), [
          'acceptLgpd',
          'termCode',
        ]) || getPathValue(values, ['acceptLgpd', 'termCode']);
      payload.acceptLgpd = {
        ...existing,
        evidenceType: existing.evidenceType || 'CHECKBOX',
        evidence:
          existing.evidence ||
          'Aceite realizado pelo consumidor no site Qualidoc',
        termCode: existing.termCode || termFromFields || '',
      };

      // Interplayers additionalfields / consumer.AcceptTerm expects S/N
      const consumer =
        payload.consumer && typeof payload.consumer === 'object'
          ? (payload.consumer as Record<string, unknown>)
          : {};
      const hasAcceptTermKey = flattenActivationFields(fields).some(
        (f) => f.key.toLowerCase() === 'acceptterm',
      );
      if (hasAcceptTermKey) {
        const acceptKey = flattenActivationFields(fields).find(
          (f) => f.key.toLowerCase() === 'acceptterm',
        )!.key;
        payload.consumer = {
          ...consumer,
          [acceptKey]: 'S',
        };
      }
    }

    // Final safety: never send dependent for Próprio Paciente
    if (isSelfPatientPersonType(getPersonTypeValue(payload))) {
      const consumer =
        payload.consumer && typeof payload.consumer === 'object'
          ? (payload.consumer as Record<string, unknown>)
          : null;
      if (consumer) {
        delete consumer.dependent;
        payload.consumer = consumer;
      }
    }

    onSubmit(payload);
  };

  const body = (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className="flex flex-grow flex-col gap-5"
      noValidate
    >
      <header className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-lg font-semibold text-heading">
            {ptBr('pbm-activation-title')}
          </h3>
          {attempt > 1 ? (
            <span className="rounded-full bg-accent/10 px-2.5 py-0.5 text-xs font-medium text-accent">
              {ptBr('pbm-activation-step').replace('{n}', String(attempt))}
            </span>
          ) : null}
        </div>
        <p className="text-sm leading-relaxed text-body">
          {programName
            ? ptBr('pbm-activation-subtitle-program').replace(
                '{program}',
                programName,
              )
            : ptBr('pbm-activation-subtitle')}
        </p>
      </header>

      <div className="flex flex-col gap-5">
        {visibleGroups.map(({ groupKey, fields: groupFields }) => {
          if (
            groupKey === 'acceptLgpd' ||
            groupKey === 'banner' ||
            groupKey === 'product' ||
            groupKey === 'control' ||
            groupFields.length === 0
          ) {
            return null;
          }
          const sectionFields = requiresLgpd
            ? groupFields.filter((field) => !isCoveredByLgpdCheckbox(field))
            : groupFields;
          if (sectionFields.length === 0) return null;
          return (
            <section
              key={groupKey}
              className="rounded-lg border border-border-200/70 bg-[#FAFCFE] p-3 sm:p-4"
              aria-labelledby={`pbm-act-section-${groupKey}`}
            >
              <h4
                id={`pbm-act-section-${groupKey}`}
                className="mb-3 text-sm font-semibold text-heading"
              >
                {humanizeFieldKey(groupKey)}
              </h4>
              {groupKey === 'dependent' ? (
                <p className="mb-3 text-xs leading-relaxed text-body">
                  Informe os dados do paciente para quem o benefício será
                  solicitado.
                </p>
              ) : null}
              <div className="grid grid-cols-1 gap-x-3 gap-y-3 md:grid-cols-2">
                {sectionFields.map((field) => {
                  const pathKey = field.path.join('.');
                  return (
                    <FieldControl
                      key={pathKey}
                      field={field}
                      value={getPathValue(values, field.path)}
                      error={errors[pathKey]}
                      onChange={(v) => handleChange(field.path, v)}
                      autoFocus={pathKey === firstFieldPath}
                      typedDate={prefersTypedDate}
                    />
                  );
                })}
              </div>
            </section>
          );
        })}

        {requiresLgpd ? (
          <section
            className="rounded-lg border border-accent/20 bg-[#F0F9FF] p-3 sm:p-4"
            aria-labelledby="pbm-act-lgpd-heading"
          >
            <h4
              id="pbm-act-lgpd-heading"
              className="mb-2 text-sm font-semibold text-heading"
            >
              {humanizeFieldKey('acceptLgpd')}
            </h4>
            <label className="flex cursor-pointer items-start gap-3 rounded-md border border-transparent p-1 text-sm text-body hover:border-border-200">
              <input
                type="checkbox"
                name="acceptLgpd"
                className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-accent,#009FDF)] focus-visible:ring-2 focus-visible:ring-accent/30"
                checked={lgpdAccepted}
                onChange={(e) => {
                  hasUserEditsRef.current = true;
                  setLgpdAccepted(e.target.checked);
                  setErrors((prev) => {
                    if (!prev.acceptLgpd) return prev;
                    const copy = { ...prev };
                    delete copy.acceptLgpd;
                    return copy;
                  });
                }}
                aria-invalid={Boolean(errors.acceptLgpd)}
                aria-describedby={
                  errors.acceptLgpd ? 'pbm-act-lgpd-error' : undefined
                }
              />
              <span>
                Li e aceito os{' '}
                {termsUrl ? (
                  <a
                    href={termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-accent underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
                    onClick={(e) => e.stopPropagation()}
                  >
                    termos e condições de privacidade
                  </a>
                ) : (
                  <span className="font-medium text-heading">
                    termos e condições de privacidade
                  </span>
                )}{' '}
                do programa
              </span>
            </label>
            {errors.acceptLgpd ? (
              <p
                id="pbm-act-lgpd-error"
                className="mt-2 text-xs text-red-500"
                role="alert"
              >
                {errors.acceptLgpd}
              </p>
            ) : null}
          </section>
        ) : null}
      </div>

      <div className="mt-auto flex flex-col gap-3 border-t border-border-200/60 pt-4">
        {serverError ? (
          <div
            className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700"
            role="alert"
            aria-live="assertive"
          >
            {serverError}
          </div>
        ) : null}
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex w-full items-center justify-center rounded-full bg-accent px-6 py-2.5 font-medium text-white transition-colors hover:bg-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting
            ? ptBr('pbm-activation-submitting')
            : ptBr('pbm-activation-submit')}
        </button>
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="flex items-center gap-1 self-start text-sm font-medium text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 disabled:opacity-60"
        >
          <ArrowPrevIcon className="h-5 w-5" aria-hidden="true" />
          {ptBr('text-back')}
        </button>
      </div>
    </form>
  );

  if (embedded) {
    return <PbmEmbeddedShell>{body}</PbmEmbeddedShell>;
  }

  return (
    <SquaredBox>
      <Header />
      <SquaredBox.Content className="flex flex-grow flex-col p-6">
        {body}
      </SquaredBox.Content>
    </SquaredBox>
  );
}
